/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { Delete, EditNote, MenuBook } from "@mui/icons-material";
import { Box, Chip, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { BASE_API_URL } from "../../API/Base";
import AxiosInstance from "../../API/axios";
import AddItemButton from "../../Components/AddItemButton";
import Layout from "../../Components/Layout";
import { usePagePermissions } from "../../Components/PagePermission";
import DeleteModal from "../../Modals/DeleteModal";
import { Page, WrapTextCellProps } from "../../types";

const PageMaster: React.FC = () => {
  const [rows, setRows] = useState<Page[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(40);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const navigate = useNavigate();

  const permissions = usePagePermissions(8);
  // const authData = useTokenStore.getState().AuthData;
  // const theme: Theme = useThemeStore((state) => state.theme);

  const base64Encode = (str: string) => {
    return btoa(str);
  };
  const base64Decode = (encodedStr: string) => {
    return atob(encodedStr);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  const fetchData = async (
    page: number,
    pageSize: number,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `${BASE_API_URL}/admin/modules/v1/get-all-pages`,
        {
          params: {
            page,
            per_page: pageSize,
            search_query: searchValue,
          },
        }
      );

      if (
        !response.data ||
        !response.data.pages_data ||
        response.data.total_count == null
      ) {
        console.error("Invalid response format:", response.data);
        setLoading(false);
        return;
      }

      const formattedData = response.data.pages_data.map(
        (row: Page, index: number) => ({
          ...row,
          id: row.Page_Id, // Use 'id' as the unique identifier
          actionsID: startIndex + index + 1,
        })
      );

      setRows(formattedData);
      setRowCount(response.data.total_count);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedSearchValue);
  }, [currentPage, pageSize, debouncedSearchValue]);

  // Calculate total pages
  const totalPages = Math.ceil(rowCount / pageSize);

  // Calculate start and end index for the visible rows
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(currentPage * pageSize, rowCount);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Add logic to fetch data for the new page or perform any other actions
  };

  const WrapTextCell: React.FC<WrapTextCellProps> = ({ value }) => {
    return <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>;
  };

  const handleCreate = () => {
    navigate("/pages/manage/form/create");
  };

  const ActionNamesCell = ({ value }: { value: string[] }) => (
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      {value.map((actionName) => (
        <Chip
          key={actionName}
          label={actionName}
          variant="outlined"
          style={{
            margin: 4,
            whiteSpace: "nowrap",
            color: "darkblue",
            border: "1px solid grey",
            borderRadius: 0,
          }}
          size="small"
        />
      ))}
    </div>
  );

  const handleEdit = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/pages/manage/form/edit/${encodedId}`);
  };

  const handleView = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/pages/manage/form/view/${encodedId}`);
    const actionId = permissions?.View?.id;
    const decodedId = base64Decode(encodedId);
    // logEvent(8, actionId, decodedId);
  };

  const handleDelete = (id: string) => {
    const encodedId = base64Encode(id);
    setIsDeleteModalOpen(true);
    setSelectedRowId(encodedId);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  // Conditionally define the Action column based on permissions
  const actionColumn =
    permissions?.Update || permissions?.Delete || permissions?.View
      ? {
          field: "action",
          headerName: "Action",
          width: 120,
          renderCell: (params: any) => (
            <div className="flex flex-row gap-x-1">
              {permissions?.Update && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Edit cursor-pointer"
                  onClick={() => {
                    handleEdit(params.row);
                  }}
                >
                  <Tooltip title="Edit">
                    <EditNote sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.Delete && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Delete cursor-pointer"
                  onClick={() => {
                    handleDelete(params.row.Id);
                    const actionId = permissions?.Delete?.id;
                  }}
                >
                  <Tooltip title="Delete">
                    <Delete sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.View && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-View cursor-pointer flex flex-row items-center justify-center"
                  onClick={() => {
                    handleView(params.row);
                    const actionId = permissions?.View?.id;
                  }}
                >
                  <Tooltip title="View">
                    <MenuBook sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
            </div>
          ),
        }
      : undefined;

  const coloumns: GridColDef[] = [
    {
      field: "actionsID",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
    },
    ...(actionColumn ? [actionColumn] : []),
    {
      field: "Page_Name",
      headerName: "Page Name",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Page_Url",
      headerName: "Page Url",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Module_Name",
      headerName: "Module Name",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Page_Icon",
      headerName: "Page Icon",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Page_Order",
      headerName: "Page order",
      cellClassName: "capitalize",
      width: 100,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
    {
      field: "Action_Names",
      headerName: "Permissions",
      cellClassName: "capitalize",
      width: 360,
      renderCell: (params) => <ActionNamesCell value={params.value} />,
    },
    {
      field: "IsStatus",
      headerName: "Status",
      cellClassName: "capitalize",
      width: 200,
      renderCell: (params) => (
        <div
          className={`cellWithStatus ${params.value ? "active" : "inactive"}`}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    try {
      const decodedId = selectedRowId ? base64Decode(selectedRowId) : null;
      const response = await AxiosInstance.delete(
        `${BASE_API_URL}/admin/modules/v1/pages/${decodedId}`
      );

      if (response.data) {
        // Remove the deleted row from the local state
        setIsDeleteModalOpen(false);
        setRows((prevRows) =>
          prevRows.filter((row: any) => row.Id.toString() !== decodedId)
        );
        toast.success("Data deleted successfully");
        const actionId = permissions?.Delete?.id;
        // logEvent(8, actionId, decodedId);
      } else {
        toast.error("Error deleting data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Layout PageId={3}>
      <div className="flex flex-col items-center justify-start w-full h-full">
        <div
          id="actions"
          className="flex flex-col items-center justify-between w-full md:flex-row gap-x-2"
        >
          {/* <div className="flex flex-row items-center justify-center w-full md:w-1/4 ">
            <SearchInput
              value={searchValue}
              placeholder={"Search"}
              onChange={setSearchValue}
            />
          </div> */}
          <div className="flex items-center justify-between w-full md:justify-end md:space-x-2 ">
            {permissions.Add && (
              <AddItemButton
                label="Add new"
                onClick={handleCreate}
                size="small"
              />
            )}
            {/* <ActionButtonsWithDropdown /> */}
          </div>
        </div>

        <div className="flex flex-col items-start justify-start w-full mt-2">
          <div className="w-full">
            <Box>
              <DataGrid
                rows={rows}
                columns={coloumns}
                getRowId={(row) => row.Id}
                autoHeight
                rowHeight={65}
                loading={loading}
              />
            </Box>
          </div>
        </div>

        {/* <PaginationButtons
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        /> */}
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Deletion"
          message="Are you sure you want to delete this data?"
        />
      </div>
    </Layout>
  );
};

export default PageMaster;
